import React, { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
  Location as RouterLocation,
  Navigate,
} from "react-router-dom";
import { QueryParamProvider } from "use-query-params";

import ErrorBoundary from "components/layouts/ErrorBoundary";
import { appRoutes } from "config/appRoutes.config";
import { routes } from "config/routes.config";
import AppTemplate from "components/templates/AppTemplate";
import { store } from "store/root";
import getAuthority from "utils/getAuthority";

interface RouteAdapterProps {
  children: React.FunctionComponent<{
    history: {
      replace(location: RouterLocation): void;
      push(location: RouterLocation): void;
    };
    location: RouterLocation;
  }>;
}

function RouteAdapter({ children }: RouteAdapterProps): JSX.Element | null {
  const navigate = useNavigate();
  const routerLocation = useLocation();

  const adaptedHistory = React.useMemo(
    () => ({
      replace(location: RouterLocation) {
        if (navigate) {
          navigate(location, { replace: true, state: location.state });
        }
      },
      push(location: RouterLocation) {
        if (navigate) {
          navigate(location, { replace: false, state: location.state });
        }
      },
    }),
    [navigate]
  );

  if (!children) {
    return null;
  }

  return children({ history: adaptedHistory, location: routerLocation });
}

interface AppProps {
  msalInstance: PublicClientApplication;
}

function App({ msalInstance }: AppProps): JSX.Element {
  const [roles, setRoles] = useState<string[]>(getAuthority());

  useEffect(() => {
    const getUserRoles = () => {
      const userRoles = getAuthority();
      setRoles(userRoles);
    };
    document.addEventListener("authorityChange", getUserRoles);

    return () => document.removeEventListener("authorityChange", getUserRoles);
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <Provider store={store}>
        <BrowserRouter>
          <QueryParamProvider
            ReactRouterRoute={RouteAdapter as React.FunctionComponent}
          >
            <ErrorBoundary>
              <Routes>
                <Route
                  element={
                    <>
                      <AuthenticatedTemplate>
                        <AppTemplate />
                      </AuthenticatedTemplate>
                      <UnauthenticatedTemplate>
                        <Navigate to="/login" replace />
                      </UnauthenticatedTemplate>
                    </>
                  }
                >
                  {appRoutes.map(({ name, path, authority, Element }) => {
                    const userHasRequiredRole = authority
                      ? authority.some((el) => roles.indexOf(el) !== -1)
                      : true;

                    return (
                      userHasRequiredRole && (
                        <Route key={name} path={path} element={<Element />} />
                      )
                    );
                  })}
                </Route>
                {routes.map(({ name, path, Element }) => (
                  <Route key={name} path={path} element={<Element />} />
                ))}
              </Routes>
            </ErrorBoundary>
          </QueryParamProvider>
        </BrowserRouter>
      </Provider>
    </MsalProvider>
  );
}

export default App;
